/**
 * Notification Templates List View
 *
 * Notification templates management with inline editing.
 * Templates are grouped by notification type.
 */

import { useState, useRef, useEffect } from 'react';
import { DictionariesNavigation } from '../components/DictionariesNavigation';
import {
  useNotificationTemplates,
  useUpdateNotificationTemplate,
} from '../services/notificationTemplatesApi';
import type { NotificationTemplate } from '../types/notification-template.types';

/**
 * Format notification type for display
 */
function formatNotificationType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Inline editable textarea component
 */
function EditableTextarea({
  templateId,
  notificationType,
  languageCode,
  field,
  value,
  onSave,
  isSaving,
}: {
  templateId: string;
  notificationType: string;
  languageCode: string;
  field: 'title' | 'body';
  value: string;
  onSave: (
    notificationType: string,
    languageCode: string,
    field: 'title' | 'body',
    newValue: string
  ) => void;
  isSaving: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  // Reset edit value when template value changes (after save)
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleDoubleClick = () => {
    if (!isSaving) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
    // Allow Enter for newlines in body, but Ctrl+Enter to save
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== value) {
      onSave(notificationType, languageCode, field, trimmedValue);
    }
    setIsEditing(false);
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="w-full px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px] resize-y"
          data-testid={`edit-${field}-${templateId}`}
        />
        <div className="text-xs text-gray-400 mt-1">Ctrl+Enter to save, Escape to cancel</div>
      </div>
    );
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className={`text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 -mx-2 -my-1 rounded whitespace-pre-wrap ${
        isSaving ? 'opacity-50' : ''
      }`}
      title="Double-click to edit"
      data-testid={`${field}-${templateId}`}
    >
      {value}
    </div>
  );
}

/**
 * Template row component
 */
function TemplateRow({
  template,
  onSave,
  isSaving,
  pendingUpdates,
}: {
  template: NotificationTemplate;
  onSave: (
    notificationType: string,
    languageCode: string,
    field: 'title' | 'body',
    newValue: string
  ) => void;
  isSaving: boolean;
  pendingUpdates: {
    titleTemplate: string;
    bodyTemplate: string;
  };
}) {
  const displayTitle = pendingUpdates.titleTemplate || template.title_template;
  const displayBody = pendingUpdates.bodyTemplate || template.body_template;

  return (
    <tr>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="font-medium text-sm">{template.language_code.toUpperCase()}</span>
      </td>
      <td className="px-4 py-3">
        <EditableTextarea
          templateId={template.id}
          notificationType={template.notification_type}
          languageCode={template.language_code}
          field="title"
          value={displayTitle}
          onSave={onSave}
          isSaving={isSaving}
        />
      </td>
      <td className="px-4 py-3">
        <EditableTextarea
          templateId={template.id}
          notificationType={template.notification_type}
          languageCode={template.language_code}
          field="body"
          value={displayBody}
          onSave={onSave}
          isSaving={isSaving}
        />
      </td>
    </tr>
  );
}

export function NotificationTemplatesListView() {
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [pendingUpdates, setPendingUpdates] = useState<
    Record<string, { titleTemplate: string; bodyTemplate: string }>
  >({});

  const { data: templateGroups, isLoading, error } = useNotificationTemplates();
  const updateTemplate = useUpdateNotificationTemplate();

  const toggleExpanded = (type: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleSave = (
    notificationType: string,
    languageCode: string,
    field: 'title' | 'body',
    newValue: string
  ) => {
    const key = `${notificationType}/${languageCode}`;
    setSavingKey(key);

    // Find the current template to get both values
    const group = templateGroups?.find((g) => g.notification_type === notificationType);
    const template = group?.templates.find((t) => t.language_code === languageCode);
    if (!template) return;

    // Get pending updates for this template
    const pending = pendingUpdates[key] || {
      titleTemplate: template.title_template,
      bodyTemplate: template.body_template,
    };

    // Update the pending field
    const updatedPending = {
      ...pending,
      [field === 'title' ? 'titleTemplate' : 'bodyTemplate']: newValue,
    };

    // Store pending updates locally
    setPendingUpdates((prev) => ({
      ...prev,
      [key]: updatedPending,
    }));

    updateTemplate.mutate(
      {
        notificationType,
        languageCode,
        data: {
          title_template: updatedPending.titleTemplate,
          body_template: updatedPending.bodyTemplate,
        },
      },
      {
        onSettled: () => {
          setSavingKey(null);
          // Clear pending updates after successful save
          setPendingUpdates((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading notification templates</p>
          <p className="text-gray-600">{String(error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Notification Templates</h1>
          <p className="text-gray-600 mt-1">
            Double-click a template field to edit. Templates support placeholders like{' '}
            <code className="bg-gray-100 px-1 rounded">{'{language}'}</code>.
          </p>
        </div>
      </div>

      {/* Template Groups */}
      <div className="space-y-4">
        {templateGroups?.map((group) => {
          const isExpanded = expandedTypes.has(group.notification_type);
          const templateCount = group.templates.length;

          return (
            <div
              key={group.notification_type}
              className="bg-white rounded-lg shadow-md overflow-hidden"
            >
              {/* Group Header */}
              <button
                onClick={() => toggleExpanded(group.notification_type)}
                className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  >
                    ▶
                  </span>
                  <span className="text-lg font-semibold">
                    {formatNotificationType(group.notification_type)}
                  </span>
                  <span className="text-sm text-gray-500">({templateCount} languages)</span>
                </div>
                <span className="text-sm text-gray-400 font-mono">{group.notification_type}</span>
              </button>

              {/* Templates Table */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Language
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                          Title Template
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Body Template
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {group.templates
                        .sort((a, b) => a.language_code.localeCompare(b.language_code))
                        .map((template) => {
                          const key = `${template.notification_type}/${template.language_code}`;
                          return (
                            <TemplateRow
                              key={template.id}
                              template={template}
                              onSave={handleSave}
                              isSaving={savingKey === key}
                              pendingUpdates={
                                pendingUpdates[key] || { titleTemplate: '', bodyTemplate: '' }
                              }
                            />
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {(!templateGroups || templateGroups.length === 0) && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <p className="text-gray-600 text-lg">No notification templates found.</p>
        </div>
      )}
    </div>
  );
}
